import React, { Component, ChangeEvent } from 'react';
import {
  view,
  Pagination,
  errorBoundary,
  withInject,
  ConfigService,
} from '@piwikpro/platform';
import { ListSidebarPreference } from '@piwikpro/user-preferences';
import { RouterService } from '@piwikpro/router-crate';
import {
  View,
  SplitView,
  Sidebar,
  withTranslation,
  TFunction,
} from '@piwikpro/ui-components';
import { Outlet, useLocation } from '@tanstack/react-router';
import { UserGroupListState } from '@piwikpro/user-groups-crate/redux/list';
import { UserGroup, UserGroups } from '@piwikpro/user-groups-crate';
import UserGroupsListOverlay from './UserGroupsListOverlay';

const ASCENDING = '';
const DESCENDING = '-';

/* eslint-disable camelcase */
interface UserGroupViewProps {
  config: ConfigService
  routerService: RouterService
  userPreferences: any
  userGroups: UserGroups
  filters: {
    sort: {
      by: string
      direction: '' | '-'
    }
    search: string
  }
  total: number
  isLoading: UserGroupListState['isLoading']
  failed: UserGroupListState['failed']
  Title: typeof React.Component
  t: TFunction
  configureUserGroupsFetch: any
  data: UserGroup[]
}

interface State {
  page: number
}

function UserGroupsListElement({
  id, name, caption, rightIcons, onClick,
}) {
  const location = useLocation();
  const isActive = location.pathname.includes(id);
  return (
    <Sidebar.Element
      id={id}
      name={name}
      caption={caption}
      isActive={isActive}
      rightIcons={rightIcons}
      onClick={onClick}
    />
  );
}

class UserGroupsView extends Component<UserGroupViewProps, State> {
  listSidebarPreference;

  paginationLimit: number;

  constructor(props: UserGroupViewProps) {
    super(props);

    const { config, userPreferences } = this.props;

    this.state = {
      page: 2, // first page already loads on componentDidMount
    };

    this.listSidebarPreference = new ListSidebarPreference(
      userPreferences.load(),
      '/administration/user-groups',
      {
        sortBy: config.get('USER_GROUP_DEFAULT_SORT'),
      },
    );

    this.paginationLimit = config.get('SIDEBAR_LISTS_ITEMS_PER_PAGE');

    this.moveToCreateView = this.moveToCreateView.bind(this);
    this.loadMoreListItems = this.loadMoreListItems.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.moveToDetailsView = this.moveToDetailsView.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
  }

  async componentDidMount() {
    const { config, userGroups } = this.props;

    try {
      await userGroups.find({
        sort: {
          direction: this.listSidebarPreference.sortBy === config.get('USER_GROUP_DEFAULT_SORT')
            ? ASCENDING
            : DESCENDING,
          by: this.listSidebarPreference.sortBy,
        },
      });
    // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  handleSearchChange(event: ChangeEvent<HTMLInputElement>) {
    const { configureUserGroupsFetch } = this.props;

    configureUserGroupsFetch({
      search: event.target.value,
    });
  }

  async handleSearch() {
    const { userGroups, filters } = this.props;

    if (!/^\s+$/i.test(filters.search)) { // avoid API requests for white-characters only
      this.setState({
        page: 2,
      });
      try {
        await userGroups.find({
          sort: filters.sort,
          search: filters.search.trim(),
        });
      // eslint-disable-next-line no-empty
      } catch (err) { }
    }
  }

  moveToCreateView() {
    const { routerService } = this.props;

    routerService.openAdministrationOverlay('/m/create');
  }

  moveToDetailsView(group: string | number) {
    const { routerService } = this.props;

    routerService.push({ to: '/administration/user-groups/$groupId', params: { groupId: String(group) } });
  }

  async loadMoreListItems(offset: number, limit: number) {
    const { page } = this.state;
    const { userGroups, filters } = this.props;

    this.setState({
      page: page + 1,
    });
    try {
      await userGroups.find({ ...filters, offset, limit });
    // eslint-disable-next-line no-empty
    } catch (err) { }
  }

  render() {
    const {
      data,
      userGroups,
      filters,
      t,
      config,
      total,
      isLoading,
      failed,
      Title,
      userPreferences,
    } = this.props;
    const { page } = this.state;
    let pagination;

    if (data.length !== 0) {
      pagination = Pagination.fromQueryParams(
        data,
        data.length,
        {
          limit: this.paginationLimit,
          page,
        },
      );
    }

    const listSidebarOptions = {
      sortItems: [{
        id: 'user-groups-sidebar-sort-options',
        items: [{
          name: t('user-groups.list.sortOptions.name'),
          value: 'name',
          id: 'name',
        },
        {
          name: t('user-groups.list.sortOptions.addedAt'),
          value: 'created_at',
          id: 'created_at',
        },
        {
          name: t('user-groups.list.sortOptions.updatedAt'),
          value: 'updated_at',
          id: 'updated_at',
        }],
      }],
      onSortByItemSelected: async (sortByValue: string) => {
        this.listSidebarPreference.sortBy = sortByValue;

        userPreferences.persist(this.listSidebarPreference);

        this.setState({
          page: 2,
        });
        try {
          await userGroups.find({
            sort: {
              by: this.listSidebarPreference.sortBy,
              direction: this.listSidebarPreference.sortBy === 'name' ? ASCENDING : DESCENDING,
            },
            search: filters.search,
          });
        // eslint-disable-next-line no-empty
        } catch (err) { }
      },
      sortDefault: filters.sort.by,
    };

    const getLabelForUserGroup = (group: any) => {
      let result;

      switch (filters.sort.by) {
        case 'created_at':
          result = t('components:date.short', { date: new Date(group.attributes.created_at) });
          break;
        case 'updated_at':
          result = t('components:date.short', { date: new Date(group.attributes.updated_at) });
          break;
        default:
          result = null;
          break;
      }

      return result;
    };

    return (
      <Title part={t('user-groups.title')}>
        <SplitView
          leftSidebar={{
            canBeHideManually: true,
            sidebar: (
              <div style={{ width: '400px', height: '100%' }}>
                <Sidebar>
                  <Sidebar.Search
                    value={filters.search}
                    placeholder={t('user-groups.list.searchPlaceholderWithExternalId')}
                    onSearchChange={this.handleSearchChange}
                    onSearch={this.handleSearch}
                    debounceTime={config.get('SIDEBAR_LISTS_SEARCH_DELAY')}
                  />
                  <Sidebar.ListOptions
                    sort={{
                      list: listSidebarOptions.sortItems,
                      onChange: listSidebarOptions.onSortByItemSelected,
                      value: this.listSidebarPreference.sortBy,
                    }}
                  />
                  {!isLoading && data.length === 0 && (
                    <Sidebar.Empty type="subdued">
                      {t('user-groups.list.no-items')}
                    </Sidebar.Empty>
                  )}
                  {failed && (
                    <Sidebar.Empty type="error">
                      {t('ListSidebar.error')}
                    </Sidebar.Empty>
                  )}
                  {isLoading ? (
                    <Sidebar.ElementsLoading />
                  ) : (
                    <Sidebar.List>
                      {data.map(group => (
                        <UserGroupsListElement
                          id={group.id}
                          key={group.id}
                          name={group.attributes.name}
                          caption={getLabelForUserGroup(group)}
                          rightIcons={group.attributes.external_id ? [
                            {
                              name: 'group-mapping',
                              tooltip: {
                                content: t('user-groups.list.icon.externalId-tooltip-content') as string,
                              },
                            },
                          ] : []}
                          onClick={this.moveToDetailsView}
                        />
                      ))}
                    </Sidebar.List>
                  )}
                  {data.length < total && (
                    <Sidebar.LoadMore
                      onClick={() => this.loadMoreListItems(pagination.offset, this.paginationLimit)}
                    />
                  )}
                  <Sidebar.AddSection
                    mainButton={{
                      onClick: this.moveToCreateView,
                      text: t('user-groups.list.createButton'),
                    }}
                  />
                </Sidebar>
              </div>
            ),
          }}
        >
          <View fullWidth fullHeight>
            <View.Body>
              <Outlet />
              <UserGroupsListOverlay />
            </View.Body>
          </View>
        </SplitView>
      </Title>
    );
  }
}

export default withInject({
  config: 'config',
  userPreferences: 'userPreferences',
  userGroups: 'UserGroupsCrate.userGroups',
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
})(
  view({ role: 'OWNER' })(withTranslation('administration')(
    errorBoundary(UserGroupsView as any),
  )),
);
